const OPTIONS_SPECIFICATION_ALIASES = {
  panel_for_filter: 'panel',
  refreshrate: 'refresh_rate',
  resolution_for_filter: 'resolution'
};
const OPTIONS_HIDDEN_SPECIFICATION_KEYS = new Set([
  'firmware_links',
  'schema_product',
  'user_manual_page',
  'user_manuals'
]);
const OPTIONS_SPECIFICATION_ORDER = [
  'screen_size',
  'panel',
  'resolution',
  'refresh_rate',
  'response_time',
  'aspect_ratio',
  'brightness',
  'contrast_ratio',
  'color_gamut',
  'color_gamut_volume',
  'hdr_support',
  'low_blue_light',
  'ports',
  'adjustment',
  'power',
  'product_size',
  'product_weight'
];
const OPTIONS_SPECIFICATION_LABELS = {
  adjustment: 'Adjustment',
  aspect_ratio: 'Aspect Ratio',
  brightness: 'Brightness',
  color_gamut: 'Color Gamut',
  color_gamut_volume: 'Color Gamut Volume',
  contrast_ratio: 'Contrast Ratio',
  hdr_support: 'HDR Support',
  low_blue_light: 'Low Blue Light',
  panel: 'Panel',
  ports: 'Ports',
  power: 'Power',
  product_size: 'Product Size',
  product_weight: 'Product Weight',
  refresh_rate: 'Refresh Rate',
  resolution: 'Resolution',
  response_time: 'Response Time',
  screen_size: 'Screen Size'
};

function getOptionsUnqualifiedSpecificationKey(key) {
  return String(key || '').split('.').pop();
}

function isOptionsHiddenSpecificationKey(key) {
  return OPTIONS_HIDDEN_SPECIFICATION_KEYS.has(getOptionsUnqualifiedSpecificationKey(key));
}

function getOptionsPlainText(value) {
  const stringValue = String(value || '');
  if (!stringValue.includes('<')) return stringValue.trim();

  if (typeof DOMParser === 'undefined') {
    return stringValue.replace(/<[^>]*>/g, '').trim();
  }

  return new DOMParser().parseFromString(stringValue, 'text/html').body.textContent.trim();
}

function parseOptionsJsonObject(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return {};

  try {
    const parsedValue = JSON.parse(value);
    return parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue) ? parsedValue : {};
  } catch (error) {
    return {};
  }
}

function getOptionsConfiguredSpecifications() {
  const structureElement = document.getElementById('comparison-structure');
  if (!structureElement) return [];

  try {
    const structure = JSON.parse(structureElement.textContent || '[]');
    if (!Array.isArray(structure)) return [];

    return structure
      .map((item) => {
        const key = String(item?.key || '').trim();
        const label = String(item?.label || '').trim();
        return key && label ? { key, label } : null;
      })
      .filter((item) => item && !isOptionsHiddenSpecificationKey(item.key));
  } catch (error) {
    return [];
  }
}

function hasOptionsSpecificationValue(value) {
  if (Array.isArray(value)) return value.some((item) => hasOptionsSpecificationValue(item));
  if (value === null || value === undefined) return false;
  if (typeof value !== 'string') return true;

  const normalizedValue = value.trim();
  return normalizedValue !== '' && normalizedValue !== '-' && normalizedValue !== '\u2013' && normalizedValue !== '\u2014';
}

function normalizeOptionsSpecificationValue(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeOptionsSpecificationValue(item))
      .filter((item) => hasOptionsSpecificationValue(item));
  }

  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean' || typeof value === 'number') return value;
  if (typeof value === 'object') return getOptionsPlainText(JSON.stringify(value));

  return getOptionsPlainText(value);
}

function normalizeOptionsSpecifications(value) {
  const source = parseOptionsJsonObject(value);
  const specifications = {};

  Object.entries(source).forEach(([key, rawValue]) => {
    const unqualifiedKey = getOptionsUnqualifiedSpecificationKey(key);
    if (isOptionsHiddenSpecificationKey(key) || (key === unqualifiedKey && OPTIONS_SPECIFICATION_ALIASES[key])) return;

    const normalizedValue = normalizeOptionsSpecificationValue(rawValue);
    if (hasOptionsSpecificationValue(normalizedValue)) specifications[key] = normalizedValue;
  });

  Object.entries(OPTIONS_SPECIFICATION_ALIASES).forEach(([alias, canonicalKey]) => {
    if (hasOptionsSpecificationValue(specifications[canonicalKey])) return;

    const normalizedValue = normalizeOptionsSpecificationValue(source[alias]);
    if (hasOptionsSpecificationValue(normalizedValue)) specifications[canonicalKey] = normalizedValue;
  });

  return specifications;
}

function getOptionsSpecificationKeys(products) {
  const availableKeys = new Set();

  products.forEach((product) => {
    Object.keys(product.specifications).forEach((key) => availableKeys.add(key));
  });

  const orderedKeys = OPTIONS_SPECIFICATION_ORDER.filter((key) => availableKeys.delete(key));

  return [...orderedKeys, ...Array.from(availableKeys).sort()].filter((key) => {
    return products.some((product) => hasOptionsSpecificationValue(product.specifications[key]));
  });
}

function getOptionsSpecificationValue(specifications, key) {
  const unqualifiedKey = getOptionsUnqualifiedSpecificationKey(key);
  const namespaceSeparatorIndex = key.indexOf('.');
  const namespace = namespaceSeparatorIndex > 0 ? key.slice(0, namespaceSeparatorIndex) : '';
  const canonicalKey = OPTIONS_SPECIFICATION_ALIASES[unqualifiedKey];
  const candidateKeys = [key];

  if (unqualifiedKey !== key) candidateKeys.push(unqualifiedKey);
  if (canonicalKey) {
    if (namespace) candidateKeys.push(`${namespace}.${canonicalKey}`);
    candidateKeys.push(canonicalKey);
  }

  const matchedKey = candidateKeys.find((candidateKey) => hasOptionsSpecificationValue(specifications[candidateKey]));
  return matchedKey ? specifications[matchedKey] : undefined;
}

function getOptionsSpecificationDefinitions(products) {
  const configuredSpecifications = getOptionsConfiguredSpecifications();

  if (configuredSpecifications.length > 0) {
    return configuredSpecifications.filter((specification) => {
      return products.some((product) => hasOptionsSpecificationValue(getOptionsSpecificationValue(product.specifications, specification.key)));
    });
  }

  return getOptionsSpecificationKeys(products).map((key) => ({
    key,
    label: getOptionsSpecificationLabel(key)
  }));
}

function getOptionsSpecificationLabel(key) {
  if (OPTIONS_SPECIFICATION_LABELS[key]) return OPTIONS_SPECIFICATION_LABELS[key];

  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function getOptionsSpecificationDisplayValue(value) {
  if (!hasOptionsSpecificationValue(value)) return '\u2014';
  if (Array.isArray(value)) {
    return value
      .filter((item) => hasOptionsSpecificationValue(item))
      .map((item) => getOptionsSpecificationDisplayValue(item))
      .join(', ');
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

class YGComparisonOptions extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.selects = Array.from(this.querySelectorAll('[data-yg-comparison-options-select]'));
    this.productContainers = Array.from(this.querySelectorAll('[data-yg-comparison-options-product-content]'));
    this.scrollContainer = this.querySelector('.yg-comparison-options__scroll');
    this.parameters = this.querySelector('[data-yg-comparison-options-parameters]');
    this.toggle = this.querySelector('[data-yg-comparison-options-toggle]');
    this.toggleText = this.querySelector('[data-yg-comparison-options-toggle-text]');
    this.collapsedRows = Math.max(1, Number.parseInt(this.dataset.collapsedRows || '5', 10) || 5);
    this.expanded = false;
    this.products = new Map();
    this.scrollStartPosition = this.scrollContainer?.scrollLeft || 0;

    this.querySelectorAll('[data-yg-comparison-options-product-template]').forEach((template) => {
      const id = String(template.dataset.productId || '');
      if (!id) return;

      this.products.set(id, {
        id,
        specifications: normalizeOptionsSpecifications(template.dataset.productSpecifications),
        template
      });
    });

    this.selection = String(this.dataset.defaultProductIds || '')
      .split(',')
      .map((productId) => productId.trim())
      .filter(Boolean);

    if (
      this.selects.length < 3 ||
      this.selects.length !== this.productContainers.length ||
      this.selection.length !== this.selects.length ||
      this.selection.some((id) => !this.products.has(id))
    ) {
      return;
    }

    const { signal } = this.abortController;
    this.selects.forEach((select, index) => {
      select.value = this.selection[index];
      select.addEventListener('change', () => this.handleSelectionChange(index), { signal });
    });
    this.toggle?.addEventListener('click', () => this.setExpanded(!this.expanded), { signal });
    this.scrollContainer?.addEventListener('scroll', () => this.updateStickyDivider(), {
      passive: true,
      signal
    });

    this.render();
    this.updateStickyDivider();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  updateStickyDivider() {
    const scrollDistance = Math.abs((this.scrollContainer?.scrollLeft || 0) - this.scrollStartPosition);
    this.classList.toggle('is-parameter-column-stuck', scrollDistance > 1);
  }

  handleSelectionChange(index) {
    const select = this.selects[index];
    const nextProductId = String(select.value || '');
    const previousProductId = this.selection[index];

    if (!this.products.has(nextProductId)) {
      select.value = previousProductId;
      return;
    }

    const occupiedIndex = this.selection.findIndex((productId, selectionIndex) => {
      return selectionIndex !== index && productId === nextProductId;
    });

    if (occupiedIndex !== -1) {
      this.selection[occupiedIndex] = previousProductId;
      this.selects[occupiedIndex].value = previousProductId;
      this.renderProduct(occupiedIndex);
    }

    this.selection[index] = nextProductId;
    select.value = nextProductId;
    this.renderProduct(index);
    this.renderParameters();
  }

  renderProduct(index) {
    const product = this.products.get(this.selection[index]);
    const container = this.productContainers[index];
    if (!product || !container) return;

    container.replaceChildren(product.template.content.cloneNode(true));
  }

  renderParameters() {
    if (!this.parameters) return;

    const selectedProducts = this.selection.map((productId) => this.products.get(productId));
    const specificationDefinitions = getOptionsSpecificationDefinitions(selectedProducts);
    const rows = specificationDefinitions.map((specification, index) => {
      const row = document.createElement('div');
      row.className = 'yg-comparison-options__parameter-row';
      row.setAttribute('role', 'row');
      row.hidden = !this.expanded && index >= this.collapsedRows;

      const label = document.createElement('div');
      label.className = 'yg-comparison-options__parameter-label';
      label.setAttribute('role', 'rowheader');
      label.textContent = specification.label;
      row.append(label);

      selectedProducts.forEach((product) => {
        const value = document.createElement('div');
        value.className = 'yg-comparison-options__parameter-value';
        value.setAttribute('role', 'cell');
        value.textContent = getOptionsSpecificationDisplayValue(getOptionsSpecificationValue(product.specifications, specification.key));
        row.append(value);
      });

      return row;
    });

    this.parameters.hidden = rows.length === 0;
    this.parameters.replaceChildren(...rows);
    this.updateToggle(rows.length);
  }

  setExpanded(expanded) {
    this.expanded = Boolean(expanded);

    this.parameters?.querySelectorAll('.yg-comparison-options__parameter-row').forEach((row, index) => {
      row.hidden = !this.expanded && index >= this.collapsedRows;
    });

    this.updateToggle(this.parameters?.childElementCount || 0);
  }

  updateToggle(rowCount) {
    if (!this.toggle) return;

    const hasHiddenRows = rowCount > this.collapsedRows;
    this.toggle.hidden = !hasHiddenRows;
    this.toggle.setAttribute('aria-expanded', String(this.expanded));

    if (this.toggleText) {
      this.toggleText.textContent = this.expanded
        ? String(this.dataset.viewLessText || 'View less')
        : String(this.dataset.viewMoreText || 'View more');
    }
  }

  render() {
    this.selection.forEach((productId, index) => {
      this.selects[index].value = productId;
      this.renderProduct(index);
    });
    this.renderParameters();
  }
}

if (!customElements.get('yg-comparison-options')) {
  customElements.define('yg-comparison-options', YGComparisonOptions);
}
